/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import {
  Addresses,
  InnskraningErrorMessage,
  User,
} from 'stores/onboarding/types';
import { isValidKennitala } from '@hugsmidjan/qj/kennitala';

import { ArrowButton } from 'components/button/ButtonWithIcon';
import { TextInput } from 'components/form-elements/TextInput';
import { Link } from 'components/link/Link';

import { useInnskraningSentryLogging } from 'hooks/onboarding/useInnskraningExceptionCapture';
import { useOnMount } from 'hooks/useOnMount';
import { AuthStartResponse, PollResponse, Properties } from 'types/onboarding';
import {
  InnskraningAnalyticsStepEnum,
  trackInnskraningEvent,
} from 'utils/onboarding/analytics';
import { mapOldPostalCodeToPostalCodes } from 'utils/onboarding/postalCodes';

import Notification from '../notification/Notification';
import Spinner from '../spinner/Spinner';
import { StepHeader } from '../step-header/StepHeader';

import s from './Login.module.scss';
import Tabs from 'components/tabs/Tabs';

type Props = {
  error: InnskraningErrorMessage;
  setError: (error: InnskraningErrorMessage) => void;
  clearError: () => void;
  onSubmit: () => void;
  setUser: (user: User) => void;
  setProperties: (properties: Properties) => void;
  setAddresses: (addresses: Addresses) => void;
  isKassar?: boolean;
  offerType: string | null;
};

const errorMessages = {
  default: {
    title: 'Innskráning tókst ekki',
    message: 'Ekki tókst að framkvæma auðkenningu. Vinsamlega reyndu aftur.',
  },
  timeOut: {
    title: 'Innskráning tókst ekki',
    message:
      'Tími til að staðfesta innskráningu rann út. Vinsamlegast reyndu aftur.',
  },
  guide: {
    title: 'Innskráning tókst ekki',
    message:
      'Vinsamlegast athugaðu innsláttinn og gættu þess að kveikt sé á símanum og hann sé ólæstur.',
  },
};

export const Login = (props: Props) => {
  const {
    error,
    clearError,
    onSubmit,
    setUser,
    setProperties,
    setAddresses,
    setError,
    isKassar,
    offerType,
  } = props;

  const [loading, setLoading] = useState(false);
  const [identification, setIdentification] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    clearError();
    setIdentification(event.target.value);
  };

  const { captureException } = useInnskraningSentryLogging();

  const setUserData = (res: PollResponse) => {
    if (!res?.customer || !res.customer?.ssn) {
      setError(errorMessages.default);
      return;
    }
    const newUser = res.customer;
    setUser({
      address: '',
      name: newUser.name,
      email: '',
      ssn: newUser.ssn,
      phoneNumber: newUser.phoneNumber,
      reference: '',
    });

    const netorkaProperties = JSON.parse(res?.netorkaResponse || '[]');

    setProperties(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      netorkaProperties.map((property: any) => {
        return {
          fNumber: '',
          address: property.UseplaceAddress,
          postalCode: property.UserPlacePostCode,
          city: property.UseplacePostOffice,
          selected: true,
          ean: property.ServiceIdentifier,
          netorkaMeta: property,
        };
      })
    );

    setAddresses({
      address: '',
      postalCode: '',
      oldAddress: newUser?.address,
      oldPostalCode: mapOldPostalCodeToPostalCodes(
        newUser?.postalCode,
        `${newUser?.postalCode} ${newUser?.city}`
      ),
    });

    onSubmit();
  };

  const pollAuthStatus = async (
    authRequestId: string,
    pollingInterval: number
  ) => {
    try {
      let attempts = 0;
      const maxAttempts = 30;
      const interval = pollingInterval * 1000;

      while (attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, interval));

        const pollResponse: PollResponse = await fetch('/api/onboarding/poll', {
          method: 'POST',
          body: JSON.stringify({
            authRequestId,
          }),
        }).then((res) => {
          if (res.status >= 400) {
            console.error(res);
            throw new Error('Error polling authentication status');
          }
          return res.json();
        });

        if (
          pollResponse?.responseStatus?.errorCode &&
          pollResponse.responseStatus.errorCode.length > 0
        ) {
          setError(errorMessages.default);
          setLoading(false);
          setVerificationCode('');
          return;
        }

        if (!pollResponse?.waitingForUserInput) {
          setUserData(pollResponse);
          return;
        }

        attempts++;
      }
      setError(errorMessages.timeOut);
      setLoading(false);
      setVerificationCode('');
    } catch (e) {
      captureException(e as Error, () => ({
        tags: {
          component: Login.name,
        },
        contexts: {
          data: {
            identification,
          },
          error: {
            message: `Error Polling: ${e}`,
          },
        },
      }));
      setError(errorMessages.default);

      setLoading(false);
      setVerificationCode('');
    }
  };

  const login = async () => {
    trackInnskraningEvent(InnskraningAnalyticsStepEnum.LOGIN_BUTTON_CLICK);

    clearError();
    setLoading(true);

    await fetch('/api/onboarding/startAuth', {
      method: 'POST',
      body: JSON.stringify({
        identification: identification.replace(/\D/g, ''),
      }),
    })
      .then((res) => {
        if (res.status >= 400) {
          console.error(res);
          throw new Error('Error starting authentication');
        }
        return res.json();
      })
      .then((res: AuthStartResponse) => {
        if (!res) {
          // extra cautious here as we don't want to let anyone into the flow when auth fails
          // this _should_ never happen
          throw Error('Null response from api/onboarding/startAuth');
        }

        if (
          (res?.responseStatus?.errorCode &&
            res.responseStatus.errorCode.length > 0) ||
          !res?.authRequestId
        ) {
          throw new Error(res?.responseStatus?.message || 'StartAuth error');
        }

        setVerificationCode(res?.verificationCode || '');
        pollAuthStatus(res.authRequestId, res.pollingInterval || 3);
      })
      .catch((e) => {
        captureException(e, () => ({
          tags: {
            component: Login.name,
          },
          contexts: {
            data: {
              identification,
            },
            error: {
              message: `Error StartAuth: ${e}`,
            },
          },
        }));
        setLoading(false);
        setError(errorMessages.guide);
      });
  };

  const onKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.keyCode === 13) {
      login();
    }
  };

  useOnMount(() => {
    trackInnskraningEvent(InnskraningAnalyticsStepEnum.LOGIN_PAGELOAD);
  });

  const LoginHeader = () => {
    let title = 'Fáðu stuðið heim í stofu!';
    let text = 'Það tekur aðeins tvær mínútur að ganga frá málunum.';

    if (isKassar) {
      title = 'Við sendum þér kassana';
      text =
        'Við sendum þér tíu kassa til að auðvelda flutningana — þér að kostnaðarlausu! Gildir á meðan birgðir endast. Það tekur aðeins tvær mínútur að ganga frá málunum.';
    }

    if (offerType === 'svellid') {
      title = 'Komdu í stuðið á svellinu!';
      text = 'Það tekur aðeins tvær mínútur að koma í viðskipti.';
    }

    return (
      <StepHeader title={title} subTitle="Komdu í viðskipti" text={text} />
    );
  };
  return (
    <div className={s.signIn}>
      <LoginHeader />

      <div className={s.signIn__wrapper}>
        {error.message && (
          <Notification title={error.title} type="error" onClose={clearError}>
            <p>{error.message}</p>
          </Notification>
        )}

        {loading ? (
          <>
            <Spinner centered text="Auðkenni send í síma" />
            <div className={s.signIn__code}>{verificationCode}</div>
          </>
        ) : (
          <>
            <Tabs
              activeTab={activeTab}
              onTabClick={(tabId) => {
                setErrorMessage(''), setIdentification(''), setActiveTab(tabId);
              }}
              tabs={[
                {
                  id: 'rafræn-skilríki',
                  label: 'Rafræn skilríki',
                  content: (
                    <>
                      <TextInput
                        key={activeTab}
                        icon="phone"
                        className={s.signIn__input}
                        name="tel"
                        value={identification}
                        onChange={onChange}
                        onKeyUp={onKeyUp}
                        placeholder="Sláðu inn símanúmer"
                        disabled={loading}
                        maxLength={7}
                        type="tel"
                        autoFocus={activeTab === 1}
                      />
                      <ArrowButton
                        className={s.signIn__button}
                        disabled={
                          identification === '' || identification.length !== 7
                        }
                        onClick={() => {
                          login();
                        }}
                        type="button"
                        iconRight={true}
                      >
                        Áfram
                      </ArrowButton>
                    </>
                  ),
                },
                {
                  id: 'audkennisapp',
                  label: 'Auðkennisapp',
                  content: (
                    <>
                      <TextInput
                        key={activeTab}
                        icon="fingerprint"
                        className={s.signIn__input}
                        name="ssn"
                        value={identification}
                        onChange={onChange}
                        onKeyUp={(ev) => {
                          onKeyUp(ev);
                          isValidKennitala(identification) &&
                            setErrorMessage('');
                        }}
                        placeholder="Sláðu inn kennitölu"
                        disabled={loading}
                        maxLength={10}
                        type="tel"
                        autoFocus={activeTab === 2}
                        onBlur={() =>
                          identification.length > 1 &&
                          (isValidKennitala(identification)
                            ? setErrorMessage('')
                            : setErrorMessage(
                                'Kennitala ógild: vinsamlegast athugaðu hvort kennitalan er rétt slegin inn.'
                              ))
                        }
                      />
                      <ArrowButton
                        className={s.signIn__button}
                        disabled={
                          identification === '' ||
                          identification.length !== 10 ||
                          isValidKennitala(identification) === false
                        }
                        onClick={() => {
                          login();
                        }}
                        type="button"
                        iconRight={true}
                      >
                        Áfram
                      </ArrowButton>
                      <p className={s.error}>{errorMessage}</p>
                    </>
                  ),
                },
              ]}
            />
          </>
        )}

        <p className={s.signIn__terms}>
          Með því að skrá þig inn samþykkir þú að upplýsingar um þig séu
          meðhöndlaðar í samræmi við{' '}
          <Link
            to="https://www.orkusalan.is/personuverndarstefna"
            target="_blank"
            rel="noopener noreferrer"
          >
            persónuverndarstefnu
          </Link>{' '}
          Orkusölunnar.
          <br />
          <br />
          Einnig leyfir þú öflun upplýsinga frá Netorku um þá staði
          (heimilisföng og mæla) þar sem þú átt nú þegar í raforkuviðskiptum.
        </p>
      </div>
    </div>
  );
};
